import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AddWorkspaceMemberDocument,
	type AddWorkspaceMemberInput,
	type ArtemisUserFilterInput,
	type ArtemisWorkspaceMemberSortInput,
	DeactivateAccountDocument,
	GetMeDocument,
	GetUsersDocument,
	GetWorkspaceMembersDocument,
	RemoveWorkspaceMemberDocument,
	SetUserDisabledDocument,
	type UpdateMemberRoleInput,
	UpdateWorkspaceMemberRoleDocument,
} from "@/common/artemis-sdk/graphql";
import type { OrgUser, WorkspaceMember } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

// Users search the same way as every other list: case-insensitive `contains` over these fields.
const USER_SEARCH_FIELDS = ["email", "displayName", "id"];

/** Returns the currently authenticated user's own Artemis profile. */
export function useMe(options?: { enabled?: boolean }) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["me"],
		queryFn: async () => {
			const data = await gqlFetch(GetMeDocument);
			return data.me ?? null;
		},
		enabled: options?.enabled ?? false,
		retry: false,
	});
}

/**
 * Deactivates (soft-deletes) the caller's own account, detaching them from their org — or
 * cascade-deleting the org if they're its only admin. The caller should log out on success.
 */
export function useDeactivateAccount() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: async () => {
			await gqlFetch(DeactivateAccountDocument);
		},
	});
}

/** Fetches the first page of a workspace's members (array form, for guards/lookups). */
export function useWorkspaceMembers(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["workspaceMembers", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetWorkspaceMembersDocument, { workspaceId });
			return (data.workspaceMembers?.nodes as WorkspaceMember[]) ?? [];
		},
	});
}

/**
 * True when the current user holds the WORKSPACE_ADMIN role in this workspace. Org admins are
 * *not* covered here — combine with `useIsOrgAdmin()` where they should also pass (that is how
 * the backend grants work: WorkspaceAdmin capability set, or the org-admin blanket permit).
 */
export function useIsWorkspaceAdmin(workspaceId: string) {
	const { data: me } = useMe();
	const { data: members } = useWorkspaceMembers(workspaceId);
	return (
		!!me?.id &&
		(members?.some((m) => m.userId === me.id && m.role === "WORKSPACE_ADMIN") ??
			false)
	);
}

/** Server-side paginated workspace members: Next/Prev cursor pagination + sort. */
export function useWorkspaceMembersPaged(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<WorkspaceMember>({
		baseKey: ["workspaceMembers", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		fetchPage: async ({ sort, window }) => {
			const data = await gqlFetch(GetWorkspaceMembersDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisWorkspaceMemberSortInput>(sort),
			});
			const c = data.workspaceMembers;
			return {
				nodes: (c?.nodes as WorkspaceMember[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/** Fetches all users in the authenticated organization. Used to populate the add-member picker. */
export function useOrgUsers() {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["orgUsers"],
		queryFn: async () => {
			// GetUsersDocument has an optional $after cursor; pass explicit undefined so the call
			// compiles regardless of how TypeScript resolves the variadic conditional.
			const data = await gqlFetch(GetUsersDocument, { after: undefined });
			return (data.users?.nodes as OrgUser[]) ?? [];
		},
	});
}

/**
 * Fetches one page of org users, filtered server-side by `search` (email or display name) and paged
 * with `after` (a previous page's `endCursor`). Returns the page nodes plus `pageInfo`. Used by the
 * admin users table; {@link useOrgUsers} remains the flat lookup list for name resolution.
 */
export function useOrgUsersPage(search?: string, after?: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["orgUsersPage", search ?? null, after ?? null],
		queryFn: async () => {
			const data = await gqlFetch(GetUsersDocument, {
				where: buildWhere(search ?? "", USER_SEARCH_FIELDS, {}) as
					| ArtemisUserFilterInput
					| undefined,
				after,
			});
			return {
				nodes: (data.users?.nodes as OrgUser[]) ?? [],
				totalCount: data.users?.totalCount ?? 0,
				pageInfo: data.users?.pageInfo ?? {
					hasNextPage: false,
					endCursor: null,
				},
			};
		},
		placeholderData: (prev) => prev,
	});
}

/** Returns a function that searches org users by email or display name, optionally scoped to a workspace. */
export function useSearchUsers(workspaceId?: string) {
	const gqlFetch = useGqlClient();
	return async (search: string): Promise<OrgUser[]> => {
		if (!search.trim()) return [];
		const data = await gqlFetch(GetUsersDocument, {
			where: buildWhere(search, USER_SEARCH_FIELDS, {}) as
				| ArtemisUserFilterInput
				| undefined,
			workspaceId,
		});
		return (data.users?.nodes as OrgUser[]) ?? [];
	};
}

/** Adds a user to a workspace. Invalidates the member list on success. */
export function useAddWorkspaceMember(workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: AddWorkspaceMemberInput) => {
			const data = await gqlFetch(AddWorkspaceMemberDocument, { input });
			return data.addWorkspaceMember.workspaceMember;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["workspaceMembers", workspaceId],
			});
		},
	});
}

/** Removes a user from a workspace. Invalidates the member list on success. */
export function useRemoveWorkspaceMember(workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ userId }: { userId: string }) => {
			const data = await gqlFetch(RemoveWorkspaceMemberDocument, {
				workspaceId,
				userId,
			});
			return data.removeWorkspaceMember.success;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["workspaceMembers", workspaceId],
			});
		},
	});
}

/**
 * Marks a user disabled (e.g. left the company) or re-enables them. Disabled users stop counting
 * toward the org's active-user quota. Refreshes the user lists and org metrics on success.
 */
export function useSetUserDisabled() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			userId,
			disabled,
		}: {
			userId: string;
			disabled: boolean;
		}) => {
			const data = await gqlFetch(SetUserDisabledDocument, {
				userId,
				disabled,
			});
			return data.setUserDisabled;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["orgUsers"] });
			queryClient.invalidateQueries({ queryKey: ["orgUsersPage"] });
			queryClient.invalidateQueries({ queryKey: ["organizationMetrics"] });
		},
	});
}

/** Changes a workspace member's role. Invalidates the member list on success. */
export function useUpdateWorkspaceMemberRole(workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: UpdateMemberRoleInput) => {
			const data = await gqlFetch(UpdateWorkspaceMemberRoleDocument, { input });
			return data.updateWorkspaceMemberRole.workspaceMember;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["workspaceMembers", workspaceId],
			});
		},
	});
}
